import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Refresh, ContentCopy, Share } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import { Popup } from '@/components/common/Popup';
import { isShareAvailable, share } from '@/utilities/shareUtils';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { generateLoginURL } from '@/generated/client/token/token';
import { copyClipboard, formatEmbeddableLink } from '@/utilities/linkUtils';

import { DateTimeRangeSelect } from '@/components/common/date/DateTimeRangeSelect';
import './SharePopup.css';
import { TokenGenerateRequestType } from '@/generated/model';

export function Body({ targetId, isPhone }: { targetId: number; isPhone: boolean }) {
  const { t, i18n } = useTranslation();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [embedded, setEmbedded] = useState<boolean>(false);
  const [link, setLink] = useState<string | undefined>(undefined);
  const [dates, setDates] = useState<{ from: Date; to: Date }>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  });
  const [comment, setComment] = useState('');

  const ref = useRef(null);

  // a felirat athuzva marad :(
  // ezert bele kell focus-alni a textareaba
  // valami MUI bug
  // z-index nem segit
  function focusTextarea() {
    if (ref.current) {
      const e1 = ref.current as HTMLElement;
      const e2 = e1.firstChild as HTMLElement;
      e2.focus();
    }
  }

  async function createLink() {
    if (!isReadyToGenerate()) {
      return;
    }
    try {
      const request = {
        targetId,
        fromTimeMicros: dates.from.getTime() * 1000,
        toTimeMicros: dates.to.getTime() * 1000,
        //TODO: not used at server side, delete later
        emailAddress: 'undefined',
        type: TokenGenerateRequestType.tracking,
        comment,
      };
      const tokenReceived = (await generateLoginURL(request)).data.loginToken;
      setToken(tokenReceived);
      setLink(formatEmbeddableLink(tokenReceived, embedded, i18n));
      setTimeout(focusTextarea, 100);
    } catch (error) {
      rethrowIfNotClientException(error);
    }
  }

  function toggleLink() {
    const toogledEmbedded = !embedded;
    setEmbedded(toogledEmbedded);
    if (token) {
      setLink(formatEmbeddableLink(token, toogledEmbedded, i18n));
    }
  }

  function setSelectedDates(from: Date, to: Date) {
    setDates({ from, to });
  }

  function isReadyToGenerate(): boolean {
    if (!dates) {
      return false;
    }
    if (!dates.from || !dates.to) {
      return false;
    }
    const fromTime = dates.from.getTime();
    if (fromTime > dates.to.getTime()) {
      return false;
    }
    if (fromTime < startOfDay(new Date()).getTime()) {
      return false;
    }
    return true;
  }

  async function shareLink() {
    if (token && isShareAvailable()) {
      const shareLink = formatEmbeddableLink(token, false, i18n);
      try {
        await share({
          title: t('share.normalTitle'),
          url: shareLink,
        });
      } catch (error) {
        const ifUserAbortedShare = error instanceof DOMException && error.name == 'AbortError';
        if (ifUserAbortedShare) {
          return;
        }
        throw error;
      }
    }
  }

  return (
    <>
      <div className="share-link-datetime-range">
        <DateTimeRangeSelect
          setSelectedDates={setSelectedDates}
          startDate={dates.from}
          endDate={dates.to}
          minDateTime={startOfDay(new Date())}
        />
      </div>

      <TextField
        className={`share-link-comment ${isPhone ? 'phone' : ''}`}
        placeholder={t('share.commentPlaceholder')}
        aria-label={t('share.comment')}
        variant="outlined"
        fullWidth
        multiline
        minRows={3}
        value={comment}
        onChange={(e) => {
          setComment(e.target.value);
        }}
      />

      <Button
        variant="contained"
        disabled={!isReadyToGenerate()}
        onClick={createLink}
        className="share-link-generate"
        startIcon={<Refresh />}
      >
        {t('share.generate')}
      </Button>

      <FormControlLabel
        className="share-link-type"
        control={<Switch checked={embedded} onChange={toggleLink} />}
        label={t('share.embeddedTitle')}
      />
      <FormControl className="share-link-field" variant="outlined">
        <InputLabel shrink={link !== undefined} htmlFor="target-share-link">
          {t('share.normalTitle')}
        </InputLabel>
        <OutlinedInput
          ref={ref}
          id="target-share-link"
          type="text"
          label={t('share.normalTitle')}
          value={link}
          multiline
          rows={3}
          inputProps={{
            inputMode: 'none',
            style: { touchAction: 'pan-y' },
          }}
          disabled={token == undefined}
          endAdornment={
            <InputAdornment position="end">
              {isShareAvailable() && (
                <IconButton
                  color="primary"
                  className="share-link-copy-link"
                  disabled={token == undefined}
                  onClick={shareLink}
                  edge="end"
                >
                  <Share />
                </IconButton>
              )}
              <IconButton color="primary" disabled={token == undefined} onClick={() => copyClipboard(link)} edge="end">
                <ContentCopy />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </>
  );
}

function Header({ targetName }: { targetName: string }) {
  const { t } = useTranslation();

  return (
    <>
      <Stack>
        <Typography variant="h6">{t('share.normalTitle')}</Typography>
        <Typography sx={{ color: 'primary.main' }}>{targetName}</Typography>
      </Stack>
    </>
  );
}

export default function SharePopup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  async function closePopup() {
    await navigate(-1);
  }

  const targetIdStr = searchParams.get('targetId');
  const targetName = searchParams.get('targetName');
  if (!targetIdStr || !targetName) return null;
  const targetId = parseInt(targetIdStr, 10);
  if (isNaN(targetId) || targetId < 0) return null;
  const isPhone = isPhoneOrHighScreen();

  return (
    <>
      <Popup
        popupTitle={undefined}
        header={<Header targetName={targetName} />}
        body={<Body targetId={targetId} isPhone={isPhone} />}
        footer={undefined}
        closePopup={closePopup}
        fullHeight={false}
        isFullScr={isPhone}
        hideBackdrop={isPhone}
        leftText={true}
      />
    </>
  );
}
